/**
 * @param {Array} datos - Lista de objetos que representan los registros a mostrar en la tabla.
 * @param {Array} listaCampos - Lista de nombres de los campos que se mostrarán como columnas.
 * @param {Function} editarRegistro - Función para editar un registro cuando se active una acción. (Opcional)
 * @param {Function} manejarClickDerecho - Función que se ejecuta al hacer clic derecho sobre una fila. (Opcional)
 * @param {Boolean} mostrarChecks - Indica si se deben mostrar checkboxes en cada fila. (Opcional, por defecto `false`)
 * @param {Function} seleccionFila - Función para manejar la selección de una fila cuando se hace clic. (Opcional)
 * @param {String|Number} selectedId - ID de la fila actualmente seleccionada. (Opcional)
 */

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search } from "lucide-react"
import { useEffect, useState } from "react"
import { parseISO, format } from "date-fns"
import { FaBuilding, FaMars, FaVenus,FaCheckCircle, FaTimesCircle } from "react-icons/fa";

export default function Table({
  datos,
  listaCampos,
  editarRegistro = () => { },
  manejarClickDerecho = () => { },
  mostrarChecks = false, // Prop para controlar la visibilidad de los checkboxes
  seleccionFila,
  selectedId = null
}) {
  const [data, setData] = useState([])
  const [sorting, setSorting] = useState([])
  const [globalFilter, setGlobalFilter] = useState("")

  useEffect(() => {
    setData(datos)
  }, [datos])

  // Crear las columnas dinámicamente
  const columns = [
    ...(mostrarChecks
      ? [
        {
          id: "select",
          header: "",
          cell: (info) => (
            <input
              type="checkbox"
              onChange={() => seleccionFila(info.row.original)}
              checked={selectedId === info.row.original.id}
            />
          ),
        },
      ]
      : []),
    {
      id: "index",
      header: "",
      cell: (info) => info.row.index + 1, // Índice de la fila + 1 para numeración
    },
    ...listaCampos
      .filter((atributo) => atributo.campo !== "id") // Excluir el campo 'id'
      .map((atributo) => ({
        id: atributo.campo,
        accessorKey: atributo.campo,
        header: () => <span className="flex items-center">{atributo.titulo}</span>,
        cell: (info) => {
          const value = info.getValue()
          if (atributo.campo.toLowerCase().includes("fecha")) {
            const date = parseISO(value)
            return !isNaN(date.getTime()) ? format(date, "dd-MM-yyyy") : value
          }

          if (atributo.campo.toLowerCase().includes("numero")) {
            return value.toString().padStart(3, "0")
        }
        

          if (atributo.campo === "sujeto_metraje" || atributo.campo === "sujeto_ubicacion") {
            return value ? "✅ Sí" : "❌ No"
          }
          if (atributo.campo === "tipo_persona") {
            return value ? "RUC" : "DNI"
          }
          if (atributo.campo === "sexo") {
            return getSexoLabel(value); 
        } 
        
          if (atributo.campo === "estado") {
            return getEstadoLabel(value); 
            
          }
          return value
        },
        
      })),
  ]
  const getSexoLabel = (value) => {
    if (value === null || value === undefined) {
        return (
            <span className="text-black-500 flex items-center gap-1">
                <FaBuilding /> <span>EMPRESA</span>
            </span>
        );
    }
    return value ? (
        <span className="text-pink-500 flex items-center gap-1">
            <FaVenus /> <span>FEMENINO</span>
        </span>
    ) : (
        <span className="text-blue-500 flex items-center gap-1">
            <FaMars /> <span>MASCULINO</span>
        </span>
    );
};

const getEstadoLabel = (value) => {
  return value ? (
    <span className="text-green-500 flex items-center">
      <FaCheckCircle className="mr-1" /> <span>ACTIVO</span>
    </span>
  ) : (
    <span className="text-red-500 flex items-center">
      <FaTimesCircle className="mr-1" /> <span>INHABILITADO</span>
    </span>
  );
};

  // Configuración de la tabla
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 10, // Mostrar 10 filas por página
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
  })

  if (data.length === 0) return <p>No hay datos disponibles</p>

  return (
    <div className="flex flex-col mx-auto py-2 px-4 sm:px-4 lg:px-5">
      {/* Filtro Global */}
      <div className="mb-4 relative">
        <input
          value={globalFilter ?? ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Buscar..."
          className="w-full pl-10 pr-4 py-2 border border-gray-400 rounded-md shadow-sm"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto shadow-md border border-gray-300 rounded-lg max-h-table">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-100" >
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
                  >
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none flex items-center"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <ArrowUpDown className="ml-2" size={14} />
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={`cursor-pointer ${
                  selectedId === row.original.id ? "bg-blue-100" : "hover:bg-gray-50"
                }`}
                onDoubleClick={() => editarRegistro(row.original)}
                onContextMenu={(evento) => manejarClickDerecho(evento, row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-6 py-3 text-sm text-gray-600 whitespace-normal break-words"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>


        </table>
      </div>

      {/* Controles de Paginación */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm text-gray-900">
        <div className="flex items-center mb-4 sm:mb-0">
          <span className="mr-2">Items por página</span>
          <select
            className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 p-2"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value))
            }}
          >
            {[5, 10, 20, 30].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <button
            className="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronsLeft size={20} />
          </button>

          <button
            className="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft size={20} />
          </button>

          <span className="flex items-center">
            Página {" "}
            <input
              type="number"
              value={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0
                table.setPageIndex(page)
              }}
              className="w-16 p-2 rounded-md border border-gray-300 text-center mx-2"
            />{" "}
            de {table.getPageCount()}
          </span>

          <button
            className="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight size={20} />
          </button>

          <button
            className="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <ChevronsRight size={20} />
          </button>
        </div>
      </div>
    </div>
  )
}
